
import { Component, Prop, Vue } from "vue-property-decorator";
import Booking from "../../../domain/booking/Booking";
import Restaurant from "../../../domain/restaurant/Restaurant";
import DialogBanner from "../../app/DialogBanner.vue";
import BookingList from "@/components/booking/BookingList.vue";
import NextButton from "@/components/app/NextButton.vue";
import ConfirmationDialog from "@/components/alert/ConfirmationDialog.vue";
import BookingManualOverrideComponent, {
  manualOverrideItem
} from "@/components/booking/manualOverride/BookingManualOverrideComponent.vue";
import Room from "@/domain/restaurant/Room";
import Table from "@/domain/restaurant/Table";
import UtilService from "@/services/UtilService";
import WorkingHours from "@/domain/restaurant/WorkingHours";
import RemoteServices from "@/services/RemoteServices";
@Component({
  components: {
    BookingManualOverrideComponent,
    ConfirmationDialog,
    NextButton,
    BookingList,
    DialogBanner
  }
})
export default class BookingManualOverrideDialog extends Vue {
  @Prop({ default: false })
  value!: boolean;

  @Prop({ default: () => [] })
  bookingList!: Booking[];

  selectedBookingsCopy: manualOverrideItem[] = [];

  @Prop({ default: null })
  restaurant!: Restaurant;

  @Prop({ default: null })
  workingHours!: WorkingHours;

  groupByRoom = true;

  currentTab = 0;

  rooms: { [key: number]: Room } = {};

  tables: { [key: number]: Table } = {};

  loadingTable = false;

  created() {
    this.$store.watch(
      (state, getters) => getters.getSelectedRestaurant,
      () => {
        this.getRoomDictionary();
        this.getTableDictionary();
        this.sortTablesInRoomByName();
      }
    );
  }

  mounted() {
    this.getTableDictionary();
    this.getRoomDictionary();
  }

  get dialog() {
    return this.value;
  }

  set dialog(newValue) {
    this.$emit("input", newValue);
    this.reset();
  }

  get bookingsOnSameTableAsSelected() {
    console.log("computing bookings on same table");
    const selectedTablesId: Set<number> = new Set();
    const bookings: manualOverrideItem[] = this.selectedBookingsCopy;
    for (let i = 0; i < bookings.length; i++) {
      if (bookings[i].original.tableId != null) {
        const tableIds: number[] = [];
        tableIds.push(bookings[i].original.tableId as number);
        if (bookings[i].original.tableId !== bookings[i].changed.tableId)
          tableIds.push(bookings[i].changed.tableId as number);

        for (let j = 0; j < tableIds.length; j++) {
          const table = this.tables[tableIds[j]];
          const affectedIds = Table.getAllAffectedTableIds(table); // TODO maybe compute on creation for all tables
          for (let i = 0; i < affectedIds.length; i++)
            selectedTablesId.add(affectedIds[i]);
        }
      }
    }
    return this.bookingList.filter(b => {
      if (b.tableId !== undefined)
        return (
          selectedTablesId.has(b.tableId) &&
          this.selectedBookings.indexOf(b) === -1
        );
      return false;
    });
  }

  get confirmationDialog() {
    return this.$refs.confirmationDialog as ConfirmationDialog;
  }

  selectedAll(selectionChange: { items: Booking[]; value: boolean }) {
    for (let i = 0; i < selectionChange.items.length; i++)
      this.bookingSelected({
        item: selectionChange.items[i],
        value: selectionChange.value
      });
  }

  sortTablesInRoomByName() {
    for (const value of Object.values(this.rooms))
      value.tables.sort(Table.TablesByNameAscendingComparator);
  }

  bookingSelected(selectionChange: { item: Booking; value: boolean }) {
    if (selectionChange.value) {
      const manualOverrideItem: manualOverrideItem = {
        original: selectionChange.item,
        changed: Booking.copy(selectionChange.item),
        selectedRoom: this.originalRoom(selectionChange.item)
      };
      manualOverrideItem.changed.tableId = undefined;
      this.selectedBookingsCopy.push(manualOverrideItem);
    } else {
      this.selectedBookingsCopy = this.selectedBookingsCopy.filter(
        (b: manualOverrideItem) =>
          b.original.bookingId !== selectionChange.item.bookingId
      );
    }
  }

  originalRoom(booking: Booking): Room | null {
    if (booking.tableId !== undefined)
      if (this.tables[booking.tableId] != null)
        return this.rooms[this.tables[booking.tableId].roomId];
    return new Room();
  }

  showTablePicker() {
    // this.selectedBookingsCopy.sort(Booking.sortBookingByTime);
    this.currentTab = 1;
  }

  async goBack() {
    if (this.currentTab === 0) {
      if (
        await this.confirmationDialog.open(
          this.$tc("booking.dialog.close.title"),
          this.$tc("booking.dialog.close.message")
        )
      ) {
        this.dialog = false;
      }
    } else {
      this.currentTab -= 1;
    }
  }

  async getTableDictionary() {
    const rest = await this.$store.getters.getSelectedRestaurant;
    if (rest != null) this.tables = UtilService.getTableDict(rest);
    else this.tables = {};
  }

  async getRoomDictionary() {
    const rest = await this.$store.getters.getSelectedRestaurant;
    if (rest != null) this.rooms = UtilService.getRoomDict(rest);
    else this.rooms = {};
    this.sortTablesInRoomByName();
  }

  closeAndFetchBookings() {
    this.dialog = false;
    this.$emit("update-booking");
  }

  get selectedBookings() {
    const toReturn: Booking[] = [];
    for (let i = 0; i < this.selectedBookingsCopy.length; i++)
      toReturn.push(this.selectedBookingsCopy[i].original);
    return toReturn;
  }

  async cancelManualOverride() {
    if (
      await this.confirmationDialog.open(
        this.$tc("booking.manualOverride.cancel.label"),
        this.$tc("booking.manualOverride.cancel.message")
      )
    ) {
      try {
        await RemoteServices.cancelManualOverride(
          this.workingHours,
          this.selectedBookings
        );
        await this.$store.dispatch(
          "success",
          this.$t("booking.manualOverride.cancel.success")
        );
        this.closeAndFetchBookings();
      } catch (error) {
        this.$store.dispatch("error", error.message);
      }
    }
  }

  reset() {
    this.selectedBookingsCopy = [];
    this.currentTab = 0;
  }
}
